import * as React from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/ui/components/ui/button"
import { ShoppingCartIcon } from "@/ui/components/icons"
import { cn } from "@/lib/utils"

export interface TrophyRoomEmptyStateProps {
  /**
   * Empty state variant
   * - owner: Authenticated user viewing their own Trophy Room (shows shop button)
   * - viewer: Public visitor viewing someone else's Trophy Room (message only)
   */
  variant: "owner" | "viewer"

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * Trophy Room Empty State Component
 *
 * Displays an empty state when a Trophy Room has no items.
 * Supports two variants for different contexts:
 * - Owner: Shows messaging with CTA button to shop
 * - Viewer: Shows informational message without CTA
 *
 * Design specs from Figma:
 * - Gap: 2rem (32px) between elements
 * - Text: 0.9375rem (15px)
 * - Button: 2.5rem (40px) height
 * - Layout: Centered with vertical flex
 *
 * @example Owner variant
 * ```tsx
 * <TrophyRoomEmptyState variant="owner" />
 * ```
 *
 * @example Viewer variant
 * ```tsx
 * <TrophyRoomEmptyState variant="viewer" />
 * ```
 */
export const TrophyRoomEmptyState = React.forwardRef<
  HTMLDivElement,
  TrophyRoomEmptyStateProps
>(({ variant, className }, ref) => {
  const { t } = useTranslation()
  const isOwner = variant === "owner"

  const handleShopClick = () => {
    window.open("https://anyw3ar.com", "_blank")
  }

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col gap-[2rem] items-center text-center mt-8",
        className
      )}
    >
      {isOwner ? (
        <>
          {/* Owner: Message 1 */}
          <p className="font-montserrat font-normal text-[0.9375rem] text-gray-700 leading-[1.25rem]">
            {t(
              "trophyRoom.empty.message1",
              "Looks like your wardrobe is still empty."
            )}
          </p>

          {/* Owner: Message 2 */}
          <p className="font-montserrat font-normal text-[0.9375rem] text-gray-700 leading-[1.25rem]">
            {t(
              "trophyRoom.empty.message2",
              "Visit anyw3ar.com to claim your first piece."
            )}
          </p>

          {/* Owner: Shop Button with ShoppingCartIcon */}
          <Button
            variant="primary-dark"
            size="primary-dark"
            leftIcon={<ShoppingCartIcon size={10} />}
            onClick={handleShopClick}
            className="w-full h-[2.5rem]"
          >
            {t("trophyRoom.empty.shopButton", "GO TO THE SHOP")}
          </Button>
        </>
      ) : (
        <>
          {/* Viewer: Single message */}
          <p className="font-montserrat font-normal text-[0.9375rem] text-gray-700 leading-[1.25rem]">
            {t(
              "publicTrophyRoom.empty.message",
              "No item here you could go to anyw3ar.com to see what could have been here"
            )}
          </p>

          {/* Viewer: Shop Button with ShoppingCartIcon */}
          <Button
            variant="primary-dark"
            size="primary-dark"
            leftIcon={<ShoppingCartIcon size={10} />}
            onClick={handleShopClick}
            className="w-full h-[2.5rem]"
          >
            {t("publicTrophyRoom.empty.shopButton", "GO TO THE SHOP")}
          </Button>
        </>
      )}
    </div>
  )
})

TrophyRoomEmptyState.displayName = "TrophyRoomEmptyState"
